import React, { ChangeEvent, Component } from "react"
import { Button, Col, Form, FormLabel, Modal, OverlayTrigger, Popover, Spinner } from "react-bootstrap"
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { FaChevronRight, FaInfoCircle } from "react-icons/fa"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import CollectionSite from "../../api/Location/LocationModel/CollectionSite"
import { stateAbbreviationsToCommonWealthState, StateToTimeZone } from "../../api/Location/LocationModel/CommonWealthStateTextMapping"
import { APIError } from "../../api/ObservableFromFetch"
import OrderCancel from "../../api/OrderRequests/OrderCancel"
import OrderResultRecord from "../../api/OrderResults/OrderResultModel/OrderResultRecord"
import CollectionSiteInfoGet from "../../api/Vendor/CollectionSiteInfoGet"
import FormatError from "../../formatters/FormatError"
import FormatPhone from "../../formatters/FormatPhone"
import ActionButton from "../ActionButton"
import ConfirmationModal from "../ConfirmationModal"
import CalculateDateStrings from "../OrderRequests/CalculateDateStrings"
import SiteSearch from "../SiteSearch/SiteSearch"
import { SetTestDetailVendorParameters } from "../../api/OrderRequests/SSOOrderRequest"
import OrderUpdate from "../../api/OrderRequests/OrderUpdate"
import { CurrentServiceDetails } from "../../api/OrderResults/CurrentServiceDetails"
import ServiceDetails from "../../api/OrderResults/OrderResultModel/ServiceDetails"
import { TimeZoneMapping } from "../sso/SiteSearchPage"
import Checkbox from "../Checkbox"
import ExpirationDate from "../ExpirationDate"

interface OrderEditModalProps {
  details?: OrderResultRecord
  onHide: () => void
  onOrderUpdated: () => void
}

interface OrderEditModalState {
  loading: boolean
  data?: ServiceDetails
  clinicLoading: boolean
  cancelling: boolean
  saving: boolean
  clinic?: CollectionSite
  newClinic?: CollectionSite
  expirationDate: Date | undefined
  timezone: string | undefined
  showSiteSearch: boolean
  showConfirmCancelOrderModal: boolean
  showConfirmSaveModal: boolean
  proceedImmediately: boolean
  selectedExpirationDate: Date | undefined
  extendExpirationHours: number
}

export default class OrderEditModal extends Component<OrderEditModalProps, OrderEditModalState> {
  state: OrderEditModalState = {
    loading: false,
    clinicLoading: false,
    cancelling: false,
    saving: false,
    showSiteSearch: false,
    showConfirmCancelOrderModal: false,
    showConfirmSaveModal: false,
    expirationDate: undefined,
    timezone: undefined,
    proceedImmediately: false,
    selectedExpirationDate: undefined,
    extendExpirationHours: 0
  }

  expirationDate = React.createRef<DatePicker>()
  subscription?: Subscription
  loadClinicSubscription?: Subscription
  cancelSubscription?: Subscription
  saveSubscription?: Subscription

  componentDidMount() {
    const { details } = this.props
    const { FormFoxVendorAN, CollectionVendorID, TestDetailClinicalID, TestDetailID } = details || { FormFoxVendorAN: undefined, CollectionVendorID: undefined, TestDetailID: undefined, TestDetailClinicalID: undefined }
    if (FormFoxVendorAN || CollectionVendorID) {
      this.setState({ clinicLoading: true })
      this.loadClinicSubscription = CollectionSiteInfoGet(FormFoxVendorAN, CollectionVendorID).subscribe(
        data => {
          const clinic = data?.Name ? data : undefined
          const timezone = this.calculateTimezone(clinic)
          this.setState({
            clinicLoading: false,
            clinic: clinic,
            showSiteSearch: false,
            timezone: data?.TimeZone || timezone
          })
        },
        error => this.handleAPIError(error, { clinicLoading: false })
      )
    }

    if (TestDetailID || TestDetailClinicalID) {
      this.setState({ loading: true })
      this.subscription = CurrentServiceDetails({ TestDetailClinicalID: TestDetailClinicalID, TestDetailID: TestDetailID || "" }).subscribe(
        (data: ServiceDetails) => {
          const selectedExpirationDate = data.EventData.ExpirationDate ? new Date(data.EventData.ExpirationDate) : undefined
          selectedExpirationDate?.setHours(selectedExpirationDate.getHours() - data.EventData.ExtendExpirationHours)
          
          this.setState({
            data: data,
            loading: false,
            expirationDate: data.EventData.ExpirationDate ? new Date(data.EventData.ExpirationDate) : undefined,
            proceedImmediately: data.EventData.ProceedImmediately,
            selectedExpirationDate,
            extendExpirationHours: data.EventData.ExtendExpirationHours
        })},
        (error) => this.handleAPIError(error, { loading: false })
      )
    }
  }

  componentWillUnmount() {
    this.subscription && this.subscription.unsubscribe()
    this.loadClinicSubscription && this.loadClinicSubscription.unsubscribe()
    this.cancelSubscription && this.cancelSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error(FormatError(error))
  }

  onClinicChange = (site: CollectionSite) => {
    const timezone = this.calculateTimezone(site)
    this.setState({
      newClinic: site,
      showSiteSearch: false,
      timezone: site.TimeZone || timezone
    })
  }

  addHoursToCurrentDate = (hours: number) => {
    const newDate = new Date()
    newDate.setHours(newDate.getHours() + hours)
    return newDate
  }

  calculateTimezone = (clinic?: CollectionSite) => {
    const stateAbbr = clinic?.State
    if (stateAbbr) {
      const state = stateAbbreviationsToCommonWealthState[stateAbbr]
      return StateToTimeZone[state]
    }
    return undefined
  }

  performCancelOrder = () => {
    this.setState({ cancelling: true, showConfirmCancelOrderModal: false })
    this.cancelSubscription && this.cancelSubscription.unsubscribe()
    this.cancelSubscription = OrderCancel(this.props.details?.TestDetailID, this.props.details?.TestDetailClinicalID, this.props.details?.FormFoxTestAN).subscribe(
      data => {
        toast.success(data)
        this.props.onOrderUpdated()
      },
      error => this.handleAPIError(error, { cancelling: false })
    )
  }

  performSave = () => {
    this.setState({ saving: true, showConfirmSaveModal: false })
    this.saveSubscription && this.saveSubscription.unsubscribe()

    const { clinic, newClinic, expirationDate, timezone, proceedImmediately } = this.state
    const { details, onOrderUpdated } = this.props

    const site = newClinic || clinic
    const formFoxTestAN: number = parseInt(details?.FormFoxTestAN || "0");
    const formattedExpirationDate = expirationDate ? CalculateDateStrings(expirationDate, timezone).formattedDate : undefined

    const timeZoneName = TimeZoneMapping[timezone || ""]
    const parameters: SetTestDetailVendorParameters = {
      formFoxTestAN: formFoxTestAN,
      collectionVendorID: site?.VendorID || 0,
      formFoxVendorAN: site?.FormFoxVendorAN || 0,
      FFVendorCode: site?.Code || "",
      collectorVendorName: site?.Name || "",
      servicePriceTiers: [],
      expirationDate: formattedExpirationDate,
      sendLink: false,
      sendCallback: false,
      timeZone: timeZoneName || "",
      isFFOrder: false,
      ReferenceTestID: 0,
      ProceedImmediately: proceedImmediately,
      ExtendExpirationHours: this.state.extendExpirationHours,
      IsMarketPlace: site?.IsMarketPlace || false,
      ClinicWasUpdated: !!newClinic,
    }
    this.saveSubscription = OrderUpdate(parameters).subscribe(
      data => {
        toast.success(data)
        onOrderUpdated()
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  proceedImmediatelyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const enabled = !!e.target.checked
    this.setState({ proceedImmediately: enabled })
  }

  calculateExpirationDate = (date: Date | undefined, extendHours: number, toggle: boolean) => {
    if (!date) {
      return
    }

    const newExpirationDate = new Date(date)
    newExpirationDate.setHours(newExpirationDate.getHours() + (toggle ? extendHours : -extendHours))
    
    return newExpirationDate
  }

  handleChangeExtendExpirationHours = (e: ChangeEvent<HTMLInputElement>) => {
    const hours = Number(e.target.value)
    const checked = e.target.checked

    this.setState((prev) => {
      const currentExpirationDate = prev.expirationDate ? new Date(prev.expirationDate) : new Date()
      return {
        ...prev,
        expirationDate: this.calculateExpirationDate(currentExpirationDate, hours, checked),
        extendExpirationHours: checked ? hours : 0,
      }
    })
  }

  handleChangeExpirationDate = (date: Date) => {
    const extendExpirationHours = date ? this.state.extendExpirationHours : 0

    this.setState((prev) => ({ 
      ...prev,
      selectedExpirationDate: date || undefined,
      extendExpirationHours: extendExpirationHours,
      expirationDate: this.calculateExpirationDate(date, extendExpirationHours, extendExpirationHours > 0),
    }))
  }

  render() {
    const {
      clinicLoading,
      cancelling,
      saving,
      clinic,
      newClinic,
      expirationDate,
      timezone,
      showSiteSearch,
      showConfirmCancelOrderModal,
      showConfirmSaveModal,
      loading,
      data,
      proceedImmediately,
      extendExpirationHours
    } = this.state
    const { details, onHide } = this.props

    const clinicToDisplay = newClinic || clinic
    const formFoxTestAN: number = parseInt(details?.FormFoxTestAN || "0");

    return <>
      {showSiteSearch && <Modal show={true} onHide={() => this.setState({ showSiteSearch: false })} size="xl" dialogClassName="dark modal-90w">
        <Modal.Header closeButton>
          <Modal.Title>SITE SEARCH</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SiteSearch formFoxTestAN={formFoxTestAN} isSSO={false} forceShowFilter defaultAddress="" onSiteSelect={this.onClinicChange} />
        </Modal.Body>
      </Modal>}
      <Modal show onHide={onHide} size="lg" dialogClassName="dark">
        <Modal.Header closeButton>
          <Modal.Title>
            <h4 className="text-uppercase"><span>AUTHORIZATION #{details?.FormFoxTestAN}</span></h4>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <Form className="py-2">
            {clinicLoading && <Form.Row>
              <Col>
                <Spinner animation="border" variant="secondary" />
              </Col>
            </Form.Row>}
            {!clinicLoading && <>
              <Form.Row>
                <Form.Label column className="font-weight-bold">Clinic</Form.Label>
              </Form.Row>
              {clinicToDisplay && <>
                <Form.Row>
                  <Col>
                    <Form.Label className="font-weight-bold">{clinicToDisplay?.Name}</Form.Label>
                  </Col>
                </Form.Row>
                <Form.Row>
                  <Col>
                    <Form.Label>{clinicToDisplay?.Address1} {clinicToDisplay?.Address2} {clinicToDisplay?.Zip}</Form.Label>
                  </Col>
                </Form.Row>
                {(!!clinicToDisplay?.Phone || !!clinicToDisplay?.Fax) && <Form.Row>
                  <Col>
                    {clinicToDisplay?.Phone && <Form.Label className="pr-3">Phone: {FormatPhone(clinicToDisplay?.Phone)}</Form.Label>}
                    {clinicToDisplay?.Fax && <Form.Label>Fax: {FormatPhone(clinicToDisplay?.Fax)}</Form.Label>}
                  </Col>
                </Form.Row>
                }
              </>}
              <Form.Row className="pt-2">
                <Col>
                  <Button variant="secondary" className="fixed-w10 btn-icon" disabled={cancelling || saving} onClick={() => this.setState({ showSiteSearch: true })}>
                    {(clinic || newClinic) ? "CHANGE CLINIC" : "FIND CLINIC"}
                    <FaChevronRight />
                  </Button>
                </Col>
              </Form.Row>
              <Form.Row className="pt-4">
                <Form.Label column className="font-weight-bold">SCHEDULE</Form.Label>
              </Form.Row>
              <Form.Row>
                <Form.Label column className="font-weight-bold col-4">Clinic Time Zone</Form.Label>
                <Form.Label column>{timezone}</Form.Label>
              </Form.Row>
              {/* <Form.Row>
                <Form.Label column className="font-weight-bold col-4">Hide Expiration Date</Form.Label>
                <Col xs={4} className="align-self-start d-flex justify-content-start">
                  {<Checkbox id="proceedImmediately" checked={!!proceedImmediately} onChange={this.proceedImmediatelyChange} title={"hide expiration date"} />}
                </Col>
              </Form.Row> */}
              <Form.Row>
                <Form.Label column sm={5} className="font-weight-bold col-4">
                  Expiration Date
                </Form.Label>
                <Col>
                  {proceedImmediately ? 
                    <>Proceed Immediately to the Collection Site</>
                    :
                    <>
                      <ExpirationDate
                        expirationDate={this.state.selectedExpirationDate}
                        timezone={timezone}
                        disabled={cancelling || saving}
                        onChange={this.handleChangeExpirationDate}
                      />
                      {!!extendExpirationHours && expirationDate && (
                        <OverlayTrigger placement="top-start" delay={{ show: 100, hide: 400 }} overlay={
                          <Popover id="extend_expiration_hours">
                            <Popover.Title as={"h3"}>Extended Expiration Date</Popover.Title>
                            <Popover.Content>
                              {expirationDate.toLocaleString('en-US', { 
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: 'numeric',
                                  minute: 'numeric'
                                })}
                            </Popover.Content>
                          </Popover>}>
                          <FaInfoCircle className="info-icon" />
                        </OverlayTrigger>
                      )}
                    </>
                  }
                </Col>
              </Form.Row>
              {!proceedImmediately && (
                <Form.Row>
                  <FormLabel column sm={5} className="font-weight-bold" htmlFor="extend_expiration_hours">
                    Extend Expiration by 24 Hours
                  </FormLabel>
                  <Col className="d-flex align-items-center">
                    <Checkbox 
                      id="extend_expiration_hours"
                      label=""
                      value='24'
                      disabled={!expirationDate}
                      className="abc-checkbox"
                      checked={expirationDate && extendExpirationHours > 0}
                      onChange={this.handleChangeExtendExpirationHours}
                    />
                  </Col>
                </Form.Row>)}
            </>}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {!!data && <ActionButton variant="secondary" size="sm" disabled={clinicLoading || saving || loading} disableOnSpin={true} spin={cancelling} className="mr-auto" onClick={() => this.setState({ showConfirmCancelOrderModal: true })}>Cancel Order</ActionButton>}
          <ActionButton variant="primary" size="sm" disabled={clinicLoading || cancelling || !(newClinic || expirationDate || proceedImmediately)} disableOnSpin={true} spin={saving} onClick={() => this.setState({ showConfirmSaveModal: true })}>Save Changes</ActionButton>
          <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal show={showConfirmCancelOrderModal}
        title="CANCEL CONFIRMATION"
        textTitle={!!data?.IsRandom ? "Orders placed in the Selection Inbox may not be cancelled." : "Are you sure you want to cancel the Order?"}
        text=""
        disableConfirmButton={!!data?.IsRandom}
        onConfirm={this.performCancelOrder}
        onHide={() => this.setState({ showConfirmCancelOrderModal: false })} />
      <ConfirmationModal show={showConfirmSaveModal}
        title="ORDER UPDATE CONFIRMATION"
        textTitle="Are you sure you want to Save changes?"
        text=""
        onConfirm={this.performSave}
        onHide={() => this.setState({ showConfirmSaveModal: false })} />
    </>
  }
}
